import { useMemo, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { Card, CardContent, CardHeader } from '@/components/common/Card'
import { Field } from '@/components/patching/FormParts'
import { ScopeFields } from '@/components/patching/ScopeFields'
import { TargetFields } from '@/components/patching/TargetFields'
import { useCreatePatchCampaign, usePatchMe } from '@/hooks/usePatching'
import {
  buildCampaignRequest,
  defaultFormValues,
  emptyDrafts,
  hasErrors,
  validateCampaignRequest,
  validatePendingDrafts,
  type CampaignFormValues,
  type DraftKey,
  type FieldErrors,
  type ScopeDrafts,
} from '@/utils/patchValidation'
import type { PatchCampaign } from '@/types/patching'
import shared from '@/components/patching/Patching.module.css'
import styles from './PatchCampaignFormPage.module.css'

interface InventoryDraft {
  name?: string
  description?: string
  resource_group?: string
  vm_names?: string[]
  kb_include?: string[]
}

function valuesFromInventory(state: unknown): CampaignFormValues {
  const base = defaultFormValues()
  if (!state || typeof state !== 'object' || !('inventoryDraft' in state)) return base
  const draft = (state as { inventoryDraft?: InventoryDraft }).inventoryDraft
  if (!draft) return base
  const names = (draft.vm_names ?? []).map((name) => name.trim()).filter(Boolean)
  return {
    ...base,
    name: draft.name ?? base.name,
    description: draft.description ?? base.description,
    kb_include: draft.kb_include ?? base.kb_include,
    environments: {
      ...base.environments,
      dev: {
        ...base.environments.dev,
        included: true,
        resource_group: draft.resource_group ?? '',
        vm_names: names.length > 0 ? names : [''],
      },
    },
  }
}

/** Create-campaign form (contract §9 POST campaigns/). Client validation mirrors the server; server field errors are shown too. */
export function PatchCampaignFormPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: me } = usePatchMe()
  const create = useCreatePatchCampaign()

  const [values, setValues] = useState<CampaignFormValues>(() => valuesFromInventory(location.state))
  const [clientErrors, setClientErrors] = useState<FieldErrors>({})
  const [serverErrors, setServerErrors] = useState<FieldErrors>({})
  const [serverMessage, setServerMessage] = useState<string | null>(null)
  const [created, setCreated] = useState<PatchCampaign | null>(null)
  // Text typed into a multi-entry input but not yet committed with Add / Enter.
  const [drafts, setDrafts] = useState<ScopeDrafts>(emptyDrafts)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  // Recomputed live once a submit was attempted, so the message disappears as soon as the user adds the value.
  const pendingErrors = useMemo(() => (submitAttempted ? validatePendingDrafts(drafts) : {}), [submitAttempted, drafts])

  const errorsFor = useMemo(
    () => (path: string) => {
      const out: string[] = []
      for (const source of [clientErrors, pendingErrors, serverErrors]) {
        for (const [key, messages] of Object.entries(source)) {
          if (key === path || key.startsWith(`${path}.`)) {
            for (const m of messages) if (!out.includes(m)) out.push(m)
          }
        }
      }
      return out
    },
    [clientErrors, pendingErrors, serverErrors],
  )

  const update = (patch: Partial<CampaignFormValues>) => setValues((prev) => ({ ...prev, ...patch }))
  const updateDraft = (key: DraftKey, value: string) => setDrafts((prev) => ({ ...prev, [key]: value }))
  const prodIncluded = values.environments.prod.included
  const notPermitted = me !== undefined && !me.can_request

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    setServerErrors({})
    setServerMessage(null)
    setSubmitAttempted(true)
    const request = buildCampaignRequest(values)
    const errors = validateCampaignRequest(request)
    setClientErrors(errors)
    // A value typed but never added would silently be left out of the scope (and its hash).
    if (hasErrors(errors) || hasErrors(validatePendingDrafts(drafts))) return
    create.mutate(request, {
      onSuccess: (campaign) => setCreated(campaign),
      onError: (err) => {
        setServerErrors(err.fieldErrors)
        setServerMessage(err.message)
      },
    })
  }

  const clientErrorCount = [clientErrors, pendingErrors].reduce(
    (total, source) => total + Object.values(source).reduce((n, m) => n + m.length, 0),
    0,
  )

  if (created) {
    return (
      <div className={styles.page}>
        <Card>
          <CardHeader title="Campaign created" />
          <CardContent className={shared.stack}>
            <div className={clsx(shared.alert, shared.alertInfo)} data-testid="created-banner">
              <CheckCircle2 size={16} className={shared.alertIcon} />
              <span>
                <strong>{created.id}</strong> — {created.name} was created. Nothing has been dispatched yet: plan each
                environment from the campaign page.
              </span>
            </div>
            <div>
              <span className={shared.factLabel}>Scope hash returned by the portal (SHA-256)</span>
              <p className={shared.hash} data-testid="created-scope-hash">{created.scope_hash || '—'}</p>
              <span className={shared.hint}>Every approval is bound to this hash; changing the scope or targets voids approvals.</span>
            </div>
            <div className={shared.actionRow}>
              <Button onClick={() => navigate(`/patch-management/${created.id}`)}>Open campaign</Button>
              <Button variant="secondary" onClick={() => navigate('/patch-management')}>Back to campaigns</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <form className={styles.page} onSubmit={onSubmit} noValidate aria-label="New patch campaign">
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <ShieldCheck size={20} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>New patch campaign</h1>
            <p className={styles.pageSubtitle}>Define what to patch and where. Environments are promoted one at a time, each with its own plan and approval.</p>
          </div>
        </div>
        <Link to="/patch-management" className={styles.backLink}>
          <ArrowLeft size={14} /> Back to campaigns
        </Link>
      </div>

      {notPermitted && (
        <div className={clsx(shared.alert, shared.alertWarning)} data-testid="not-permitted">
          <AlertTriangle size={16} className={shared.alertIcon} />
          <span>Your account is not permitted to request patch campaigns. Submitting is disabled.</span>
        </div>
      )}
      {clientErrorCount > 0 && (
        <div className={clsx(shared.alert, shared.alertError)} role="alert" data-testid="form-errors">
          <AlertTriangle size={16} className={shared.alertIcon} />
          <span>Please fix {clientErrorCount} problem{clientErrorCount === 1 ? '' : 's'} highlighted below.</span>
        </div>
      )}
      {serverMessage && (
        <div className={clsx(shared.alert, shared.alertError)} role="alert" data-testid="server-error">
          <AlertTriangle size={16} className={shared.alertIcon} />
          <span>The portal rejected the request: {serverMessage}</span>
        </div>
      )}

      <Card>
        <CardHeader title="Campaign" />
        <CardContent className={shared.stack}>
          <Field id="campaign-name" label="Name" required errors={errorsFor('name')}>
            <input
              id="campaign-name"
              className={clsx(shared.input, errorsFor('name').length > 0 && shared.inputError)}
              value={values.name}
              maxLength={200}
              onChange={(e) => update({ name: e.target.value })}
            />
          </Field>
          <Field id="campaign-product" label="Product" required hint="Product key, for example ross." errors={[...errorsFor('product'), ...errorsFor('patch_scope.product')]}>
            <input
              id="campaign-product"
              className={clsx(shared.input, errorsFor('product').length > 0 && shared.inputError)}
              value={values.product}
              onChange={(e) => update({ product: e.target.value })}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
          <Field id="campaign-description" label="Description" errors={errorsFor('description')}>
            <textarea
              id="campaign-description"
              className={shared.textarea}
              value={values.description}
              maxLength={2000}
              onChange={(e) => update({ description: e.target.value })}
            />
          </Field>
          <Field
            id="campaign-ticket"
            label="Change ticket"
            required={prodIncluded}
            hint="Required when a PROD target is included."
            errors={errorsFor('change_ticket')}
          >
            <input
              id="campaign-ticket"
              className={clsx(shared.input, errorsFor('change_ticket').length > 0 && shared.inputError)}
              value={values.change_ticket}
              onChange={(e) => update({ change_ticket: e.target.value })}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Patch scope" subtitle="Identical across environments and bound to every approval." />
        <CardContent>
          <ScopeFields values={values} onChange={update} drafts={drafts} onDraftChange={updateDraft} errorsFor={errorsFor} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Targets" subtitle="For each environment, list the VMs or name a tag. Never a wildcard. The Azure subscription is taken from the environment configuration, never from this request." />
        <CardContent>
          <TargetFields values={values} onChange={update} errorsFor={errorsFor} />
        </CardContent>
      </Card>

      <div className={shared.actionRow}>
        <Button type="submit" loading={create.isPending} disabled={notPermitted}>
          Create campaign
        </Button>
        <Button type="button" variant="secondary" onClick={() => navigate('/patch-management')} disabled={create.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
